<template>
    <div class="dashboard">
        <div class="dashboardHide" v-show="isProfile" @click.stop="changeProfile(false)"></div>
        
        <!-- Mobile Menu Overlay -->
        <div class="dashboard_mobileOverlay" v-show="isMobileMenuOpen" @click.stop="closeMobileMenu()"></div>

        <div class="dashboard_header">
            <NavbarHeader ref="navbarHeaderRef" />
        </div>

        <div class="dashboard_content">
            <transition name="menuSlide" mode="out-in">
                <div class="dashboard_menu" v-if="!isMinimize" key="menu-open" ref="menuRef" :class="{ 'dashboard_menu--mobile': isMobileMenuOpen }">
                    <!-- Header Mobile - Logo e Actions - APENAS MOBILE -->
                    <template v-if="isMobile">
                        <div class="dashboard_menu_mobileHeader">
                            <div class="dashboard_menu_mobileLogo">
                                <img src="../../assets/images/logo/logo.png" alt="FileSave Logo">
                            </div>
                        </div>
                        
                        <!-- Botão Adicionar Arquivo - Mobile -->
                        <div class="dashboard_menu_mobileButton">
                            <sw-button
                                name="Adicionar arquivo"
                                icon="plus"
                                color="blue"
                                @click="openUploadModal"
                            />
                        </div>
                    </template>
                    
                    <Menus>
                        <Menu 
                            name="Dashboard"
                            icon="meter"
                            :router="{name: 'Dashboard'}" />

                        <Menu
                            name="Meus Arquivos"
                            icon="folder"
                            :router="{name: 'MyFiles'}"
                        />

                        <Menu
                            name="Automação"
                            :isSubMenu="true"
                            icon="robots"
                            >
                            <SubMenu
                                name="Envios Automáticos"
                                :router="{name: 'AutomationEmails'}"/>
                        </Menu>

                        <Menu
                            name="Páginas Web"
                            :isSubMenu="true"
                            icon="earth"
                            >
                            <SubMenu
                                name="Compartilhamento"
                                :router="{name: 'WebPagesSharing'}"/>
                            <SubMenu
                                name="Área de Upload"
                                :router="{name: 'WebPagesUpload'}"/>
                        </Menu>

                        <Menu
                            name="Notificações"
                            :isSubMenu="true"
                            icon="bell"
                            >
                            <SubMenu
                                name="Alertas de Vencimento"
                                :router="{name: 'ExpirationAlerts'}"/>
                        </Menu>

                        <Menu
                            name="Relatórios"
                            icon="stats-bars"
                            :router="{name: 'Reports'}" />

                    </Menus>

                    <UserPlanBadge
                        :plan="currentUser?.plan"
                        @upgrade-click="navigateToUpgrade"
                    />

                    <!-- Novidades Menu -->
                    <div class="dashboard_menu_novidades">
                        <div class="novidades_card">
                            <div class="novidades_header" @click="navigateToRoadmap">
                                <div class="novidades_icon">
                                    <sw-icon name="star-full" />
                                </div>
                                <div class="novidades_content">
                                    <h4>Novidades</h4>
                                    <span>Veja o que há de novo na FileSave</span>
                                </div>
                                <div class="novidades_badge">
                                    <span>New</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Sugestões Menu -->
                    <div class="dashboard_menu_sugestoes">
                        <div class="sugestoes_card">
                            <div class="sugestoes_header" @click="navigateToSuggestions">
                                <div class="sugestoes_icon">
                                    <sw-icon name="magic-wand" />
                                </div>
                                <div class="sugestoes_content">
                                    <h4>Sugestões</h4>
                                    <span>Conte suas ideias para melhorarmos</span>
                                </div>
                                <div class="sugestoes_arrow">
                                    <sw-icon name="arrow-right" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else key="menu-collapsed" class="dashboard_menu_collapsed">
                    <div class="collapsed_trigger" @click="changeMinimize(false)" :title="'Expandir menu'">
                        <sw-icon name="arrow-right" />
                    </div>
                </div>
            </transition>

            <div class="dashboard_container" :class="{ 'dashboard_container--minimized': isMinimize }">        
                <div class="dashboard_container_content">
                    <!-- <transition name="routerFade" mode="out-in"> -->
                        <router-view v-if="menuActiveName != 'Dashboard'"/>
                        <Home v-else />
                    <!-- </transition> -->
                </div>
            </div>
        </div>

        <transition name="profile">
            <Profile v-if="isProfile" />
        </transition>
        
    </div>
</template>

<script setup>
    import { computed, ref, onMounted, onUnmounted, provide } from 'vue';
    import { useStore } from 'vuex';
    import { useRouter } from 'vue-router';

    import { fetchCurrentUser } from '@/services/mockApi';

    import NavbarHeader from './NavbarHeader';
    import Menus from '@Component/menus/MenusComponent';
    import Menu from '@Component/menus/MenuComponent';
    import SubMenu from '@Component/menus/SubMenuComponent';
    import Profile from '@Component/container/ProfileComponent';
    import Home from '@View/Dashboard/Home';
    import UserPlanBadge from '@Component/UserPlanBadge';

    const store = useStore();
    const router = useRouter();

    const currentUser = ref(null);
    const isMobile = ref(false);
    const menuRef = ref(null);
    const scrollTimeout = ref(null);

    const menuActiveName = computed(() => store.state.menuActiveName);
    const isMinimize = computed(() => store.state.isMinimize);
    const isProfile = computed(() => store.state.isProfile);
    const isMobileMenuOpen = computed(() => store.state.isMobileMenuOpen);

    const changeProfile = (value) => {
        store.commit('changeProfile', value);
    };

    const changeMinimize = (value) => {
        store.commit('changeMinimize', value);
    };

    const closeMobileMenu = () => {
        store.commit('closeMobileMenu');
    };

    const navbarHeaderRef = ref(null);

    const openUploadModal = () => {
        // Chamar a função openUploadModal do NavbarHeader
        if (navbarHeaderRef.value) {
            navbarHeaderRef.value.openUploadModal();
        }
    };

    // Disponibilizar a função openUploadModal para componentes filhos
    provide('openUploadModal', openUploadModal);

    const navigateToUpgrade = () => {
        router.push({ name: 'PlanUpgrade' });
        // Fechar menu mobile se estiver aberto
        if (isMobileMenuOpen.value) {
            closeMobileMenu();
        }
    };

    const navigateToRoadmap = () => {
        router.push({ name: 'News' });
        // Fechar menu mobile se estiver aberto
        if (isMobileMenuOpen.value) {
            closeMobileMenu();
        }
    };

    const navigateToSuggestions = () => {
        router.push({ name: 'Suggestions' });
        // Fechar menu mobile se estiver aberto
        if (isMobileMenuOpen.value) {
            closeMobileMenu();
        }
    };

    // Detectar se é mobile
    const checkMobile = () => {
        isMobile.value = window.innerWidth <= 768;
    };

    const handleResize = () => {
        checkMobile();
    };

    const loadCurrentUser = async () => {
        try {
            const response = await fetchCurrentUser();
            if (response.status === 'success') {
                currentUser.value = response.data;
            }
        } catch (error) {
            console.error('Erro ao carregar dados do usuário:', error);
        }
    };

    const handleMenuScroll = () => {
        if (!menuRef.value) return;

        // Adicionar classe de scrolling
        menuRef.value.classList.add('dashboard_menu--scrolling');

        // Limpar timeout anterior se existir
        if (scrollTimeout.value) {
            clearTimeout(scrollTimeout.value);
        }

        // Definir novo timeout de 3 segundos
        scrollTimeout.value = setTimeout(() => {
            if (menuRef.value) {
                menuRef.value.classList.remove('dashboard_menu--scrolling');
            }
        }, 3000);
    };

    onMounted(() => {
        loadCurrentUser();
        checkMobile();
        window.addEventListener('resize', handleResize);

        // Aguardar próximo tick para garantir que DOM está montado
        setTimeout(() => {
            if (menuRef.value) {
                menuRef.value.addEventListener('scroll', handleMenuScroll);
            }
        }, 100);
    });

    onUnmounted(() => {
        window.removeEventListener('resize', handleResize);

        // Limpar evento de scroll e timeout
        if (menuRef.value) {
            menuRef.value.removeEventListener('scroll', handleMenuScroll);
        }
        if (scrollTimeout.value) {
            clearTimeout(scrollTimeout.value);
        }
    });
</script>

<style lang="scss" scoped>
@import '@/assets/css/mediaScreen.scss';
    .dashboard {
        display: flex;
        flex-direction: column;
        background: var(--backgroundCard);
        width: 100%;
        height: 100vh;
        min-height: 100vh;
        overflow: hidden;
    }

    .dashboard_header {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 10; /* Reduzido para ficar abaixo do modal */
        background: var(--backgroundCard);
        border-bottom: 1px solid var(--dashboardBorderColor);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .dashboardHide{
        position: fixed;
        top: 0px;
        left: 0px;
        width: 100%;
        height: 100%;
        background: var(--backgroundOverlay);
        z-index: 20; /* Acima do menu mas abaixo do modal (99) */            
    }

    .dashboard_menu{
        position: fixed;
        left: 0;
        top: 60px; /* Abaixo do cabeçalho */
        bottom: 0;
        width: 310px;
        background: var(--backgroundCard);
        border-right: 1px solid var(--dashboardBorderColor);
        z-index: 5; /* Reduzido para ficar abaixo do modal */
        overflow-y: auto;
        padding-top: 3em; /* Desktop mantém padding normal */

        /* Scrollbar sempre visível */
        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.3) transparent;

        /* Webkit browsers (Chrome, Safari, Edge) */
        &::-webkit-scrollbar {
            width: 8px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.3);
            border-radius: 4px;
            transition: background 0.3s ease;

            &:hover {
                background: rgba(0, 0, 0, 0.5);
            }
        }
    }

    /* No desktop, garantir que elementos mobile não afetem layout */
    @media (min-width: 769px) {
        .dashboard_menu {
            padding-top: 3em; /* Manter espaçamento desktop */
        }
    }

    .dashboard_content{
        display: flex;
        flex: 1;
        margin-top: 60px; /* Altura do cabeçalho */
    }

    .dashboard_container{
        position: fixed;
        top: 60px; /* Abaixo do cabeçalho */
        right: 0;
        bottom: 0;
        left: 310px; /* Largura do menu aberto */
        background: var(--backgroundDashboard);
        overflow-y: auto;
        overflow-x: hidden;
    }

    .dashboard_container_content{
        padding: 3em 20px 30px 20px; /* 3em no topo para alinhar com o menu */
        min-height: 100%;
        width: 100%;
    }

    /* Quando menu está minimizado */
    .dashboard_container--minimized {
        left: 60px; /* Largura do menu minimizado */
    }

    /* Animações do menu */
    .menuSlide-enter-active {
        transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    }
    
    .menuSlide-leave-active {
        transition: all 0.3s cubic-bezier(0.55, 0, 0.55, 0.2);
    }
    
    .menuSlide-enter-from {
        transform: translateX(-100%);
        opacity: 0;
    }
    
    .menuSlide-leave-to {
        transform: translateX(-100%);
        opacity: 0;
    }

    /* Transição suave para o conteúdo quando menu minimiza */
    .dashboard_container {
        transition: left 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    }

    .dashboard_menu_collapsed {
        position: fixed;
        left: 0;
        top: 60px; /* Abaixo do cabeçalho */
        bottom: 0;
        width: 60px;
        background: var(--backgroundCard);
        border-right: 1px solid var(--dashboardBorderColor);
        z-index: 5; /* Reduzido para ficar abaixo do modal */
        display: flex;
        align-items: flex-start;
        padding-top: 3em; /* Alinhado com o menu aberto */
        
        .collapsed_trigger {
            width: 100%;
            display: flex;
            justify-content: center;
            padding: 1em;
            cursor: pointer;
            color: var(--textMuted);
            transition: var(--transitionDefault);
            border-radius: var(--borderRadiusMedium);
            margin: 0 0.5em;
            
            &:hover {
                background: var(--backgroundHover);
                color: var(--linkColor);
                transform: translateX(0.25em);
            }
            
            .sw-icon {
                font-size: 1.25em;
                animation: pulse 2s infinite;
            }
        }
    }
    
    @keyframes pulse {
        0%, 100% {
            opacity: 0.7;
            transform: scale(1);
        }
        50% {
            opacity: 1;
            transform: scale(1.1);
        }
    }

    /* Novidades Menu Styles */
    .dashboard_menu_novidades {
        padding: 1.5em 2em 2em 2em;
        border-top: 1px solid var(--borderColorLight);

        .novidades_card {
            background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(139, 92, 246, 0.05));
            border: 1px solid rgba(59, 130, 246, 0.1);
            border-radius: 16px;
            padding: 1.5em;
            position: relative;
            overflow: hidden;

            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 3px;
                background: linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899);
                animation: shimmer 3s ease-in-out infinite;
            }
        }

        .novidades_header {
            display: flex;
            align-items: center;
            gap: 1em;
            position: relative;
            cursor: pointer;
            transition: all 0.3s ease;
            border-radius: 12px;
            padding: 0.5em;

            &:hover {
                background: rgba(59, 130, 246, 0.05);
                transform: translateY(-2px);
            }

            .novidades_icon {
                width: 40px;
                height: 40px;
                border-radius: 12px;
                background: linear-gradient(135deg, #3b82f6, #8b5cf6);
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-size: 1.2em;
                box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);

                .sw-icon {
                    animation: sparkle 2s ease-in-out infinite;
                }
            }

            .novidades_content {
                flex: 1;

                h4 {
                    font-size: 1em;
                    font-weight: 700;
                    color: var(--textPrimary);
                    margin: 0 0 0.25em 0;
                }

                span {
                    font-size: 0.8em;
                    color: var(--textSecondary);
                    line-height: 1.3;
                }
            }

            .novidades_badge {
                position: absolute;
                top: -5px;
                right: -5px;
                background: linear-gradient(135deg, #ec4899, #f97316);
                color: white;
                padding: 0.25em 0.6em;
                border-radius: 10px;
                font-size: 0.65em;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                box-shadow: 0 2px 8px rgba(236, 72, 153, 0.4);
                animation: pulse-badge 2s ease-in-out infinite;
                z-index: 1;

                span {
                    background: linear-gradient(135deg, #ffffff, #fce7f3);
                    background-clip: text;
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
            }
        }

    }

    @keyframes shimmer {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
    }

    @keyframes sparkle {
        0%, 100% { transform: scale(1) rotate(0deg); }
        50% { transform: scale(1.1) rotate(180deg); }
    }

    @keyframes pulse-badge {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
    }

    /* Sugestões Menu Styles */
    .dashboard_menu_sugestoes {
        padding: 0 2em 2em 2em;

        .sugestoes_card {
            background: linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(34, 197, 94, 0.05));
            border: 1px solid rgba(16, 185, 129, 0.1);
            border-radius: 16px;
            padding: 1.5em;
            position: relative;
            overflow: hidden;
            transition: all 0.3s ease;

            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 8px 25px rgba(16, 185, 129, 0.1);
                background: linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(34, 197, 94, 0.08));
            }

            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 3px;
                background: linear-gradient(90deg, #10b981, #22c55e, #15803d);
                animation: shimmer 3s ease-in-out infinite;
            }
        }

        .sugestoes_header {
            display: flex;
            align-items: center;
            gap: 1em;
            cursor: pointer;
            transition: all 0.3s ease;
            border-radius: 12px;
            padding: 0.5em;

            &:hover {
                background: rgba(16, 185, 129, 0.05);

                .sugestoes_arrow {
                    transform: translateX(5px);
                }
            }

            .sugestoes_icon {
                width: 40px;
                height: 40px;
                border-radius: 12px;
                background: linear-gradient(135deg, #10b981, #22c55e);
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-size: 1.2em;
                box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);

                .sw-icon {
                    animation: bulb-glow 3s ease-in-out infinite;
                }
            }

            .sugestoes_content {
                flex: 1;

                h4 {
                    font-size: 1em;
                    font-weight: 700;
                    color: var(--textPrimary);
                    margin: 0 0 0.25em 0;
                }

                span {
                    font-size: 0.8em;
                    color: var(--textSecondary);
                    line-height: 1.3;
                }
            }

            .sugestoes_arrow {
                color: var(--colorGreen);
                font-size: 1.1em;
                transition: all 0.3s ease;
                opacity: 0.7;
            }
        }
    }

    @keyframes bulb-glow {
        0%, 100% {
            filter: brightness(1);
            transform: scale(1);
        }
        50% {
            filter: brightness(1.2);
            transform: scale(1.05);
        }
    }

    .profile-enter-active, 
    .profile-leave-active {
        transition: all 0.4s ease-out;
    }

    .profile-enter-from,
    .profile-leave-to {
        transform: translateX(100%);
        opacity: 0;
    }

    /* ===============================
       RESPONSIVE DESIGN - MOBILE
       =============================== */
    @include screen_medias(s, m) {

        .dashboard_content {
            margin-top: 59px;
        }

        /* Overlay para mobile menu */
        .dashboard_mobileOverlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 15;
            backdrop-filter: blur(2px);
        }

        /* Menu mobile responsivo */
        .dashboard_menu {
            position: fixed;
            left: -100%;
            top: 0;
            bottom: 0;
            max-width: 80vw;
            background: var(--backgroundCard);
            border-right: 1px solid var(--dashboardBorderColor);
            z-index: 20;
            overflow-y: auto;
            padding-top: 80px; /* Espaço para não sobrepor o header */
            transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);

            &.dashboard_menu--mobile {
                left: 0;
                padding-top: 0; /* Remove padding no mobile */
                width: 335px;
            }
        }


        /* Estilos mobile - aplicados apenas quando elementos existem */
        .dashboard_menu_mobileHeader {
            background: var(--backgroundCard);
            padding: 20px;
            border-bottom: 2px solid var(--dashboardBorderColor);
        }

        .dashboard_menu_mobileLogo {
            display: flex;
            justify-content: center;
            margin-bottom: 20px;
            
            img {
                width: 140px;
                height: auto;
            }
        }


        .dashboard_menu_mobileButton {
            padding: 20px;
            border-bottom: 1px solid var(--dashboardBorderColor);
            background: rgba(59, 130, 246, 0.05);
            display: flex;
            justify-content: center;
        }

        /* Novidades mobile styles */
        .dashboard_menu_novidades {
            padding: 1em 1.5em 1.5em 1.5em;
            border-top: 1px solid var(--borderColorLight);

            .novidades_card {
                padding: 1em;
            }

            .novidades_header {
                padding: 0.25em;

                .novidades_icon {
                    width: 35px;
                    height: 35px;
                    font-size: 1em;
                }

                .novidades_content h4 {
                    font-size: 0.9em;
                }

                .novidades_content span {
                    font-size: 0.75em;
                }

                .novidades_badge {
                    font-size: 0.6em;
                    padding: 0.2em 0.5em;
                    top: -3px;
                    right: -3px;
                }
            }
        }

        /* Sugestões mobile styles */
        .dashboard_menu_sugestoes {
            padding: 0 1.5em 1.5em 1.5em;

            .sugestoes_card {
                padding: 1em;
            }

            .sugestoes_header {
                padding: 0.25em;

                .sugestoes_icon {
                    width: 35px;
                    height: 35px;
                    font-size: 1em;
                }

                .sugestoes_content h4 {
                    font-size: 0.9em;
                }

                .sugestoes_content span {
                    font-size: 0.75em;
                }

                .sugestoes_arrow {
                    font-size: 1em;
                }
            }
        }

        .dashboard_menu_collapsed {
            display: none; /* Esconder menu colapsado em mobile */
        }

        /* Container ocupa toda a largura em mobile */
        .dashboard_container {
            position: fixed;
            top: 59px;
            right: 0;
            bottom: 0;
            left: 0; /* Ocupa toda largura em mobile */
            background: var(--backgroundDashboard);
            overflow-y: auto;
            overflow-x: hidden;
        }

        .dashboard_container--minimized {
            left: 0; /* Mesmo comportamento em mobile */
        }

        .dashboard_container_content {
            padding: 2em 15px 30px 15px;
        }
    }

    @include screen_s {
        .dashboard_menu {
            width: 260px;
        }

        .dashboard_container_content {
            padding: 1.5em 10px 20px 10px;
        }
    }

</style>